import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Home, Package, Truck, LogOut, Calendar, User, Menu, X, User2, LocateIcon, ChevronRight, ChevronLeft, Verified } from 'lucide-react';
import logo from '../Assets/logo1.png';
import '../Style/Dashboard.css';
import '../Style/Layout.css';
import { screenName } from '../Const/constant.js';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import AuthService from '../Api/AuthService.js';
import { toast } from 'react-toastify';
import permissionService from '../Permission/Permission.js';
import UserService from '../Api/UserService.js';
import packageJson from '../../package.json'
import { useHistoryList } from './Breadcrum/HistoryProvider.js';

// Create a context to share reset state with child components
export const LayoutContext = React.createContext({
    resetKey: 0,
    triggerReset: () => { }
});

const Layout = () => {
    const [user, setUser] = useState({ name: 'AS', role: 'Administrator' });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [accessibleScreens, setAccessibleScreens] = useState([]);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const { addCustomPath, removeCustomPath } = useHistoryList();

    // Key to force re-render/reset of child components
    const [resetKey, setResetKey] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 899);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Initialize user data and permissions
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData) {

            const screens = permissionService.getAccessibleScreens();
            setAccessibleScreens(screens);

            const currentScreen = getScreenNameFromPath(location.pathname);
            if (!permissionService.canAccessScreen(currentScreen)) {
                const firstAccessibleScreen = getFirstAccessibleScreen(screens);
                if (firstAccessibleScreen) {
                    navigate(getScreenRoute(firstAccessibleScreen));
                } else {
                    handleLogout();
                    toast.error("You don't have permissions to access any screen", { autoClose: 5000 });
                }
            }
        }
    }, [location.pathname, navigate]);

    const [userList, setUserList] = useState([]);
    const [userData, setUserData] = useState([]);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user'));
        setUserData(userData)
        fetchUsers(userData)
    }, [])

    const fetchUsers = async (userData) => {
        try {
            const data = await UserService.getAll(null, null, '');
            setUserList(data.users);
        } catch (error) {
            setUserList([]);
        }
    };

    const handleLogout = async () => {
        const user = JSON.parse(localStorage.getItem('user'));
        await AuthService.logout(user?.accessToken).then((data) => {
            localStorage.removeItem('user');
            localStorage.removeItem('user_rem');
            toast.success('Logout successfully', { autoClose: 3000 });
            navigate('/login');
        });
    };

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const confirmLogout = () => {
        setShowLogoutConfirm(false);
        handleLogout();
    };

    const cancelLogout = () => {
        setShowLogoutConfirm(false);
    };

    // Enhanced sidebar controls
    const toggleSidebar = () => {
        if (isMobile) {
            setMobileSidebarOpen(!mobileSidebarOpen);
        } else {
            setSidebarCollapsed(!sidebarCollapsed);
        }
    };

    const openSidebar = () => {
        if (isMobile) {
            setMobileSidebarOpen(true);
        } else if (sidebarCollapsed) {
            setSidebarCollapsed(false);
        }
    };

    const closeSidebar = () => {
        if (isMobile) {
            setMobileSidebarOpen(false);
        }
    };

    // Close mobile sidebar when route changes or on overlay click
    useEffect(() => {
        if (isMobile) {
            setMobileSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    // Close sidebar when clicking outside on mobile
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile && mobileSidebarOpen) {
                const sidebar = document.querySelector('.sidebar');
                const overlay = document.querySelector('.sidebar-overlay');
                if (sidebar && !sidebar.contains(event.target) && overlay && overlay.contains(event.target)) {
                    closeSidebar();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMobile, mobileSidebarOpen]);

    // Helper functions
    const getScreenNameFromPath = (path) => {
        const pathMap = {
            '/dashboard': 'dashboard',
            '/doc-master-list': 'docmaster',
            '/report': 'reports',
            '/dr-reports': 'dr-reports',
            '/print': 'print',
            '/user_management': 'users',
            '/location': 'location'
        };
        return pathMap[path] || 'dashboard';
    };

    const getScreenRoute = (screenName) => {
        const routeMap = {
            'dashboard': '/dashboard',
            'docmaster': '/doc-master-list',
            'reports': '/report',
            'dr-reports': '/dr-reports',
            'print': '/print',
            'users': '/user_management',
            'location': '/location'
        };
        return routeMap[screenName] || '/login';
    };

    const getFirstAccessibleScreen = (screens) => {
        const screenPriority = ['dashboard', 'docmaster', 'reports', 'dr-reports', 'print', 'users', 'location'];
        for (const screen of screenPriority) {
            if (screens.includes(screen)) {
                return screen;
            }
        }
        return screens[0];
    };

    const currentPage = location.pathname.split("/").filter(Boolean).pop();
    const displayName = screenName[currentPage] || currentPage;

    // Handle navigation with reset functionality
    const handleNavigation = useCallback((path) => {
        // If clicking the same page, trigger reset
        if (location.pathname === path) {
            setResetKey(prev => prev + 1); // Increment key to force re-render
        } else {
            // Navigate to different page
            navigate(path);
        }
        closeSidebar();
    }, [location.pathname, navigate]);

    // Navigation items
    const navigationItems = [
        {
            path: '/dashboard',
            name: 'dashboard',
            displayName: 'Dashboard',
            icon: Home,
            requiredPermission: 'dashboard'
        },
        {
            path: '/doc-master-list',
            name: 'docmaster',
            displayName: 'DOC Master',
            icon: Package,
            requiredPermission: 'docmaster'
        },
        {
            path: '/report',
            name: 'reports',
            displayName: 'Report',
            icon: Truck,
            requiredPermission: 'reports'
        },
        {
            path: '/dr-reports',
            name: 'dr-reports',
            displayName: 'Deviation Request',
            icon: Verified,
            requiredPermission: 'dr-reports'
        },
        {
            path: '/print',
            name: 'print',
            displayName: 'Print',
            icon: LocalPrintshopIcon,
            requiredPermission: 'print'
        },
        {
            path: '/user_management',
            name: 'users',
            displayName: 'User Management',
            icon: User2,
            requiredPermission: 'users',
            restrictedRoles: ['user'] // Roles that should not see this menu item
        },
        {
            path: '/location',
            name: 'location',
            displayName: 'Location',
            icon: LocateIcon,
            requiredPermission: 'location'
        }
    ];

    // Filter navigation items based on permissions and role restrictions
    const filteredNavigationItems = navigationItems.filter(item => {
        // First check if user has permission to access the screen
        if (!permissionService.canAccessScreen(item.requiredPermission)) {
            return false;
        }

        // Then check if this item has role restrictions
        if (item.restrictedRoles && item.restrictedRoles.length > 0) {
            // If user's role is in the restricted roles list, hide this item
            return !item.restrictedRoles.includes(permissionService.getUserRole());
        }

        return true;
    });

    // Date time state
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // Determine sidebar class based on state
    const getSidebarClass = () => {
        let className = 'sidebar';
        if (!isMobile) {
            className += sidebarCollapsed ? ' collapsed' : '';
        } else {
            className += mobileSidebarOpen ? ' mobile-visible' : ' mobile-hidden';
        }
        return className;
    };

    const currentUser = userList.find((e) => e.uid === userData.uid);

    const initials = currentUser?.name
        ?.split(" ")
        ?.map((n) => n[0])
        ?.join("")
        ?.substring(0, 2)
        ?.toUpperCase() || "U";

    return (
        <LayoutContext.Provider value={{ resetKey, triggerReset: () => setResetKey(k => k + 1) }}>
            <div className="dashboard-container">
                {/* Header */}
                <div className="dashboard-header">
                    <div className="header-left">
                        <button
                            className="mobile-menu-btn"
                            onClick={toggleSidebar}
                            aria-label="Toggle menu"
                        >
                            <Menu size={20} />
                        </button>
                        <img src={logo} style={{ width: '150px' }} alt="Company Logo" />
                    </div>

                    <h1 className="header-title">
                        {displayName}
                    </h1>

                    <div className="header-right">

                        <div className="header-info">
                            <Calendar size={16} />
                            <span>{currentDateTime.toLocaleDateString()} {currentDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="header-info" onClick={() => { /* Handle user menu click */ }}>
                            {currentUser?.avatar ? (
                                <img
                                    src={currentUser.avatar}
                                    alt="User Avatar"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.parentElement.querySelector(".avatar-fallback").style.display = "flex";
                                    }}
                                />
                            ) : null}

                            <div
                                className="avatar-fallback"
                                style={{ display: currentUser?.avatar ? "none" : "flex" }}
                            >
                                {initials}
                            </div>

                            <span>{currentUser?.name || "User"}</span>
                        </div>
                    </div>
                </div>

                <div className="dashboard-layout">
                    {/* Mobile Overlay */}
                    <div
                        className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
                        onClick={closeSidebar}
                    />

                    {/* Sidebar */}
                    <div className={getSidebarClass()}>
                        <div className="sidebar-header">
                            <div className="sidebar-brand">

                                <div className="brand-text">ATS BOM</div><button
                                    onClick={toggleSidebar}
                                    className="brand-icon"
                                    aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                >
                                    {isMobile ? <ChevronRight size={20} /> : (sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />)}
                                </button>
                            </div>
                        </div>

                        <div className="sidebar-nav">
                            <div className="nav-items">
                                {filteredNavigationItems.map((item) => {
                                    const IconComponent = item.icon;
                                    const isActive = location.pathname === item.path;
                                    return (
                                        <button
                                            key={item.path}
                                            onClick={() => { removeCustomPath(); handleNavigation(item.path) }}
                                            className={`nav-item ${isActive ? 'active' : ''}`}
                                            title={item.displayName}
                                        >
                                            <IconComponent className="nav-icon" />
                                            <span className="nav-text">{item.displayName}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="nav-logout">
                                {/* Version Display */}
                                <div className="nav-version">
                                    <span className="version-text">Version {packageJson.version}</span>
                                </div> <button onClick={handleLogoutClick} className="nav-item">
                                    <LogOut className="nav-icon" />
                                    <span className="nav-text">Log Out</span>
                                </button>


                            </div>
                        </div>
                    </div>

                    {/* Main Content with Key for Reset */}
                    <div className="main-content" key={resetKey}>
                        <Outlet />
                    </div>

                    {/* Floating Action Button for Mobile */}
                    {isMobile && (
                        <button
                            className="mobile-fab"
                            onClick={openSidebar}
                            aria-label="Open menu"
                        >
                            <Menu size={24} />
                        </button>
                    )}
                </div>

                {/* Logout Confirmation Modal */}
                {showLogoutConfirm && (
                    <div className="logout-confirm-overlay">
                        <div className="logout-confirm-dialog">
                            <div className="logout-confirm-header">
                                <h3>Confirm Logout</h3>
                            </div>
                            <div className="logout-confirm-body">
                                <p>Are you sure you want to logout?</p>
                            </div>
                            <div className="logout-confirm-footer">
                                <button
                                    className="btn-cancel"
                                    onClick={cancelLogout}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-confirm"
                                    onClick={confirmLogout}
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </LayoutContext.Provider>
    );
};

export default Layout;